import { REQUEST } from '@/constants/api';
import { LS_TOKEN_KEY } from '@/constants/common';

import { HoldersListResponse } from '@/types/api/operation';
import {
  CommonResponsePayload,
  UpdateInfoRequestPayload,
} from '@/types/api/user';
import { RequiredDocumentSection } from '@/types/document';
import { User } from '@/types/user';

import { saveLocal } from '@/utils/browser';

import { axios } from './axios';

type SetApplicationTypeArguments = {
  operationId?: string;
  applicationType: string;
};

export const setApplicationTypeRequest = async ({
  operationId,
  applicationType,
}: SetApplicationTypeArguments) => {
  const result = await axios.post<CommonResponsePayload>(
    REQUEST.OPERATION.APPLICATION_TYPE(operationId),
    {
      applicationType,
    },
  );

  return result.data;
};

export const createNewOperationRequest = async () => {
  const result = await axios.post<{
    message: string;
    access_token: string;
    token: string;
  }>(REQUEST.USER.NEW_OPERATION);

  axios.defaults.headers.common['Authorization'] =
    `Bearer ${result.data.access_token}`;
  saveLocal(LS_TOKEN_KEY, result.data.access_token);

  return result.data;
};

type AddAdultHolderDetailRequestArgs = {
  operationId: string;
  payload: AddAdultHolderDetailPayload;
};

type AddAdultHolderDetailPayload = {
  firstName: string;
  middleName?: string;
  lastName: string;
  birthday: string;
  email: string;
  phoneNumber?: string;
  relationship: string;
};

export const addAdultHolderDetailRequest = async ({
  operationId,
  payload,
}: AddAdultHolderDetailRequestArgs) => {
  const result = await axios.post<CommonResponsePayload>(
    REQUEST.OPERATION.ADD_ADULT_DETAIL(operationId),
    {
      ...payload,
      holderType: 'JOINT_ADULT',
    },
  );

  return result.data;
};

type AddAdultContactRequestArgs = {
  operationId: string;
  email: string;
};
export const addAdultContactRequest = async ({
  operationId,
  email,
}: AddAdultContactRequestArgs) => {
  const result = await axios.post<CommonResponsePayload>(
    REQUEST.OPERATION.ADD_ADULT_CONTACT(operationId),
    { email },
  );
  return result.data;
};

type AddMinorOtherInfoRequestArgs = {
  operationId: string;
  clientId: string | number;
  payload: AddMinorOtherInfoPayload;
};

type AddMinorOtherInfoPayload = {
  email?: string;
  phone?: string;
  relationship: string;
};
export const addMinorOtherInfoRequest = async ({
  operationId,
  clientId,
  payload,
}: AddMinorOtherInfoRequestArgs) => {
  const result = await axios.post<CommonResponsePayload>(
    REQUEST.OPERATION.ADULT_MINOR_RELATIONSHIP(operationId, String(clientId)),
    payload,
  );
  return result.data;
};

type ConfirmAdultPickupRequestArgs = {
  operationId: string;
};

export const confirmAdultPickupRequest = async ({
  operationId,
}: ConfirmAdultPickupRequestArgs) => {
  const result = await axios.post<CommonResponsePayload>(
    REQUEST.OPERATION.ADULT_PICKUP_CONFIRM(operationId),
    {
      adultPickup: true,
    },
  );

  return result.data;
};

export const getListHoldersIdRequest = async (operationId: string) => {
  const result = await axios.get<HoldersListResponse>(
    REQUEST.OPERATION.GET_LIST_HOLDERS_ID(operationId),
  );
  return result.data;
};

export const getHolderDetailRequest = async (
  operationId: string,
  holderId: number,
) => {
  const result = await axios.get(
    REQUEST.OPERATION.GET_HOLDER_DETAIL(operationId, holderId),
  );
  return result.data;
};

export const getHolderDetailAndRequiredDocumentsRequest = async (
  operationId: string,
  holderId: number,
) => {
  const detailResponse = await axios.get<User>(
    REQUEST.OPERATION.GET_HOLDER_DETAIL(operationId, holderId),
  );
  const requiredDocumentsResponse = await axios.get<RequiredDocumentSection[]>(
    REQUEST.OPERATION.GET_HOLDER_REQUIRED_DOCUMENTS(operationId, holderId),
  );

  return {
    user: detailResponse.data,
    requiredSections: requiredDocumentsResponse.data,
  };
};

type CancelOperationArgs = {
  operationId: string;
};

export const cancelOperation = async ({ operationId }: CancelOperationArgs) => {
  const result = await axios.post<CommonResponsePayload>(
    REQUEST.OPERATION.CANCEL(operationId),
  );

  return result.data;
};

type AdultConfirmKycRequestArgs = {
  operationId: string;
  clientId: number;
  confirmed: boolean;
};

export const adultConfirmKycRequest = async ({
  operationId,
  clientId,
  confirmed,
}: AdultConfirmKycRequestArgs) => {
  await axios.post<CommonResponsePayload>(
    REQUEST.OPERATION.ADULT_CONFIRM_KYC(operationId, clientId),
    { confirmed },
  );
};

type AdultAllowForMinorRequestArgs = {
  operationId: string;
  clientId: number;
  confirmed: boolean;
};

export const adultAllowForMinorRequest = async (
  operationId: string,
  clientId: string | number,
  payload: Partial<AdultAllowForMinorRequestArgs>,
) => {
  const result = await axios.post(
    REQUEST.OPERATION.ADULT_ALLOW_FOR_MINOR(operationId, String(clientId)),
    payload,
  );
  return result.data;
};

export const adultPepForMinorRequest = async (
  operationId: string,
  minorClientId: string | number,
  payload: Record<string, unknown>,
) => {
  const result = await axios.post(
    REQUEST.OPERATION.ADULT_PEP_FOR_MINOR(operationId, String(minorClientId)),
    payload,
  );
  return result.data;
};

export const updateHolderRequest = async (
  operationId: string,
  clientId: string | number,
  payload: Partial<UpdateInfoRequestPayload>,
) => {
  const result = await axios.patch(
    REQUEST.OPERATION.UPDATE_HOLDER(operationId, String(clientId)),
    payload,
  );
  return result.data;
};

type UpdateOperationInfoAccuracyRequestArgs = {
  operationId: string;
  clientId: number;
  payload: boolean;
};
export const updateOperationInfoAccuracy = async ({
  operationId,
  clientId,
  payload,
}: UpdateOperationInfoAccuracyRequestArgs) => {
  const result = await axios.post(
    REQUEST.OPERATION.CONFIRM_INFO_ACCURACY(operationId, clientId),
    { infoAccuracyConfirmed: payload },
  );

  return result.data;
};

type SetMinorAddressSameAsAdultArgs = {
  operationId: string;
  minorId: string | number;
  type?: string;
};

export const setMinorAddressSameAsAdult = async ({
  operationId,
  minorId,
  type,
}: SetMinorAddressSameAsAdultArgs) => {
  const result = await axios.post<CommonResponsePayload>(
    REQUEST.OPERATION.SET_ADDRESS_SAME_AS_ADULT(operationId, minorId, type),
  );

  return result.data;
};
